import { useEffect, useMemo, useState } from "react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import {
    Sliders, Calendar, ShieldCheck, Tag, Sparkles, TrendingUp,
    Check, X, Plus, Trash2, RefreshCw, Wand2,
} from "lucide-react";
import {
    AreaChart, Area, BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";

const TABS = [
    { key: "settings",    label: "Cilësimet",      icon: Sliders },
    { key: "seasons",     label: "Sezonet",        icon: Calendar },
    { key: "rules",       label: "Rregullat",      icon: ShieldCheck },
    { key: "promos",      label: "Kodet promo",    icon: Tag },
    { key: "suggestions", label: "Çmimi i zgjuar", icon: Sparkles },
    { key: "forecast",    label: "Parashikimi",    icon: TrendingUp },
];

const MODES = [
    { key: "manual",       label: "Manual",       desc: "Pa sugjerime automatike" },
    { key: "conservative", label: "Konservator",  desc: "±5-15% — mbushja + sezoni" },
    { key: "balanced",     label: "I balancuar",  desc: "±5-25% — shton ditën, ritmin, minutën e fundit" },
    { key: "aggressive",   label: "Agresiv",      desc: "±5-40% — model i plotë kërkese" },
];

export default function Revenue() {
    const [tab, setTab] = useState("settings");
    return (
        <>
            <Topbar title="Menaxhimi i të Ardhurave" subtitle="Çmim i zgjuar · Yield · Parashikim" />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                <div className="mb-4 flex flex-wrap gap-1 border-b border-border" data-testid="revenue-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                    data-testid={`revenue-tab-${t.key}`}
                                    className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                        tab === t.key ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}>
                                <Icon size={13} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                {tab === "settings"    && <SettingsTab />}
                {tab === "seasons"     && <SeasonsTab />}
                {tab === "rules"       && <RulesTab />}
                {tab === "promos"      && <PromosTab />}
                {tab === "suggestions" && <SuggestionsTab />}
                {tab === "forecast"    && <ForecastTab />}
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────
function SettingsTab() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const load = async () => {
        const { data } = await api.get("/revenue/settings");
        setS(data);
    };
    useEffect(() => { load(); }, []);
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/revenue/settings", s);
            toast.success("Cilësimet u ruajtën");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ruajtja dështoi");
        } finally { setSaving(false); }
    };
    if (!s) return <div className="text-sm text-muted-foreground">Duke u ngarkuar…</div>;
    return (
        <div className="space-y-4" data-testid="revenue-settings">
            <div className="akm-card p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Modaliteti i çmimit të zgjuar</div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
                    {MODES.map((m) => (
                        <button key={m.key} onClick={() => setS({ ...s, default_mode: m.key })}
                                data-testid={`mode-${m.key}`}
                                className={`rounded-md border p-3 text-left transition ${
                                    s.default_mode === m.key
                                        ? "border-[#ff7b02] bg-[#ff7b02]/5"
                                        : "border-border hover:border-foreground/30"
                                }`}>
                            <div className="text-sm font-semibold tracking-tight">{m.label}</div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">{m.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="akm-card p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rregullimet</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {[
                        ["weekend_pct", "Shtesa e fundjavës %", "%"],
                        ["occupancy_high_pct", "Pragu i mbushjes së lartë %", "%"],
                        ["occupancy_high_boost_pct", "Rritja në mbushje të lartë %", "%"],
                        ["occupancy_low_pct", "Pragu i mbushjes së ulët %", "%"],
                        ["occupancy_low_drop_pct", "Ulja në mbushje të ulët %", "%"],
                        ["last_minute_window_days", "Dritarja e minutës së fundit (ditë)", "d"],
                        ["last_minute_discount_pct", "Zbritja në minutën e fundit %", "%"],
                        ["early_booking_window_days", "Dritarja e rezervimit të hershëm (ditë)", "d"],
                        ["early_booking_discount_pct", "Zbritja për rezervim të hershëm %", "%"],
                        ["auto_apply_threshold_pct", "Pragu i sugjerimit %", "%"],
                    ].map(([k, label, unit]) => (
                        <Field key={k} label={label} unit={unit}>
                            <input type="number" step="0.5" value={s[k] ?? ""}
                                   onChange={(e) => setS({ ...s, [k]: parseFloat(e.target.value || 0) })}
                                   data-testid={`field-${k}`}
                                   className="akm-input w-full text-[13px]" />
                        </Field>
                    ))}
                </div>
                <div className="mt-4">
                    <button onClick={save} disabled={saving}
                            data-testid="save-settings"
                            className="akm-btn-primary">
                        {saving ? "Duke ruajtur…" : "Ruaj cilësimet"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, unit, children }) {
    return (
        <label className="block">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="relative">
                {children}
                {unit && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">{unit}</span>}
            </div>
        </label>
    );
}

// ─────────────────────────────────────────────────────────────
// Seasons
// ─────────────────────────────────────────────────────────────
function SeasonsTab() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setRows((await api.get("/revenue/seasons")).data);
    useEffect(() => { load(); }, []);
    const remove = async (id) => {
        if (!window.confirm("Të fshihet sezoni?")) return;
        await api.delete(`/revenue/seasons/${id}`);
        await load();
    };
    return (
        <div data-testid="revenue-seasons">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[11px] text-muted-foreground">{rows.length} sezon(e) të përcaktuar</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary text-[12px]" data-testid="add-season">
                    <Plus size={12} /> Sezon i ri
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Emri</th>
                            <th className="px-3 py-2 text-left">Datat</th>
                            <th className="px-3 py-2 text-right">Shumëzuesi</th>
                            <th className="px-3 py-2 text-left">Llojet e dhomave</th>
                            <th className="px-3 py-2 text-left">Qëndrimi min</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                <td className="px-3 py-2 text-muted-foreground">{r.start_date} → {r.end_date}</td>
                                <td className={`px-3 py-2 text-right font-semibold ${r.price_multiplier_pct > 0 ? "text-emerald-700" : "text-red-700"}`}>
                                    {r.price_multiplier_pct > 0 ? "+" : ""}{r.price_multiplier_pct}%
                                </td>
                                <td className="px-3 py-2 text-muted-foreground">{r.room_types?.length ? r.room_types.join(", ") : "Të gjitha"}</td>
                                <td className="px-3 py-2 text-muted-foreground">{r.min_stay || "-"}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600" data-testid={`del-season-${r.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-muted-foreground">Ende s&apos;ka sezone</td></tr>}
                    </tbody>
                </table>
            </div>
            {open && <SeasonModal onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}

function SeasonModal({ onClose, onSaved }) {
    const [f, setF] = useState({ name: "", start_date: "", end_date: "", price_multiplier_pct: 10, room_types: [], min_stay: 0 });
    const submit = async () => {
        try {
            await api.post("/revenue/seasons", f);
            toast.success("Sezoni u krijua");
            await onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Krijimi dështoi");
        }
    };
    return (
        <Modal title="Sezon i ri" onClose={onClose}>
            <div className="space-y-3">
                <Input label="Emri" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="season-name" />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Data e fillimit" type="date" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testid="season-start" />
                    <Input label="Data e mbarimit" type="date" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} testid="season-end" />
                </div>
                <Input label="Shumëzuesi %" type="number" step="1" value={f.price_multiplier_pct} onChange={(v) => setF({ ...f, price_multiplier_pct: parseFloat(v) })} testid="season-mult" />
                <Input label="Qëndrimi minimum (netë)" type="number" value={f.min_stay} onChange={(v) => setF({ ...f, min_stay: parseInt(v, 10) || 0 })} testid="season-min" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost text-[12px]">Anulo</button>
                <button onClick={submit} className="akm-btn-primary text-[12px]" data-testid="season-submit">Krijo</button>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Stay rules
// ─────────────────────────────────────────────────────────────
function RulesTab() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setRows((await api.get("/revenue/stay-rules")).data);
    useEffect(() => { load(); }, []);
    const remove = async (id) => {
        if (!window.confirm("Të fshihet rregulli?")) return;
        await api.delete(`/revenue/stay-rules/${id}`);
        await load();
    };
    const toggle = async (rule) => {
        await api.patch(`/revenue/stay-rules/${rule.id}`, { active: !rule.active });
        await load();
    };
    return (
        <div data-testid="revenue-rules">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[11px] text-muted-foreground">{rows.length} rregull(a)</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary text-[12px]" data-testid="add-rule">
                    <Plus size={12} /> Rregull i ri
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Emri</th>
                            <th className="px-3 py-2 text-left">Lloji i dhomës</th>
                            <th className="px-3 py-2 text-left">Intervali i datave</th>
                            <th className="px-3 py-2 text-right">Min</th>
                            <th className="px-3 py-2 text-right">Maks</th>
                            <th className="px-3 py-2">Ndalim shitje</th>
                            <th className="px-3 py-2">Aktiv</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                <td className="px-3 py-2 text-muted-foreground">{r.room_type || "Të gjitha"}</td>
                                <td className="px-3 py-2 text-muted-foreground">
                                    {r.date_from || "—"} → {r.date_to || "∞"}
                                </td>
                                <td className="px-3 py-2 text-right">{r.min_stay}</td>
                                <td className="px-3 py-2 text-right">{r.max_stay || "-"}</td>
                                <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                        {r.stop_sell && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700" title="Ndalim shitje">STOP</span>}
                                        {r.closed_to_arrival && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700" title="Mbyllur për ardhje">CTA</span>}
                                        {r.closed_to_departure && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700" title="Mbyllur për largim">CTD</span>}
                                        {!r.stop_sell && !r.closed_to_arrival && !r.closed_to_departure && "—"}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-center">
                                    <button onClick={() => toggle(r)} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                        {r.active ? "Aktiv" : "Joaktiv"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-muted-foreground">Ende s&apos;ka rregulla</td></tr>}
                    </tbody>
                </table>
            </div>
            {open && <RuleModal onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}

function RuleModal({ onClose, onSaved }) {
    const [f, setF] = useState({
        name: "", room_type: "", date_from: "", date_to: "",
        min_stay: 1, max_stay: 0,
        stop_sell: false, closed_to_arrival: false, closed_to_departure: false,
    });
    const submit = async () => {
        try {
            await api.post("/revenue/stay-rules", f);
            toast.success("Rregulli u krijua");
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <Modal title="Rregull i ri qëndrimi" onClose={onClose}>
            <div className="space-y-3">
                <Input label="Emri" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="rule-name" />
                <Input label="Lloji i dhomës (bosh = të gjitha)" value={f.room_type} onChange={(v) => setF({ ...f, room_type: v })} testid="rule-rt" />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Nga data" type="date" value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} testid="rule-from" />
                    <Input label="Deri në datë" type="date" value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} testid="rule-to" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Qëndrimi min" type="number" value={f.min_stay} onChange={(v) => setF({ ...f, min_stay: parseInt(v, 10) || 1 })} testid="rule-min" />
                    <Input label="Qëndrimi maks (0 = pa)" type="number" value={f.max_stay} onChange={(v) => setF({ ...f, max_stay: parseInt(v, 10) || 0 })} testid="rule-max" />
                </div>
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Kufizimet (dërgohen te Channel Manager)</div>
                    <label className="flex items-center gap-2 text-[13px]">
                        <input type="checkbox" checked={f.stop_sell} onChange={(e) => setF({ ...f, stop_sell: e.target.checked })} data-testid="rule-stop" />
                        Ndalim shitje — blloko të gjitha rezervimet në këto data
                    </label>
                    <label className="flex items-center gap-2 text-[13px]">
                        <input type="checkbox" checked={f.closed_to_arrival} onChange={(e) => setF({ ...f, closed_to_arrival: e.target.checked })} data-testid="rule-cta" />
                        Mbyllur për ardhje — klientët nuk mund të bëjnë check-in në këto data
                    </label>
                    <label className="flex items-center gap-2 text-[13px]">
                        <input type="checkbox" checked={f.closed_to_departure} onChange={(e) => setF({ ...f, closed_to_departure: e.target.checked })} data-testid="rule-ctd" />
                        Mbyllur për largim — klientët nuk mund të bëjnë check-out në këto data
                    </label>
                </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost text-[12px]">Anulo</button>
                <button onClick={submit} className="akm-btn-primary text-[12px]" data-testid="rule-submit">Krijo</button>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Promo codes
// ─────────────────────────────────────────────────────────────
function PromosTab() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setRows((await api.get("/revenue/promo-codes")).data);
    useEffect(() => { load(); }, []);
    const remove = async (id) => {
        if (!window.confirm("Të fshihet kodi promo?")) return;
        await api.delete(`/revenue/promo-codes/${id}`);
        await load();
    };
    return (
        <div data-testid="revenue-promos">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[11px] text-muted-foreground">{rows.length} kod(e)</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary text-[12px]" data-testid="add-promo">
                    <Plus size={12} /> Kod i ri promo
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Kodi</th>
                            <th className="px-3 py-2 text-left">Përshkrimi</th>
                            <th className="px-3 py-2 text-right">Zbritja</th>
                            <th className="px-3 py-2 text-left">Vlefshmëria</th>
                            <th className="px-3 py-2 text-right">Përdorur / Maks</th>
                            <th className="px-3 py-2">Statusi</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2 font-mono font-semibold">{r.code}</td>
                                <td className="px-3 py-2 text-muted-foreground">{r.description || "-"}</td>
                                <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                                    {r.discount_pct > 0 ? `${r.discount_pct}%` : formatCurrency(r.discount_amount || 0)}
                                </td>
                                <td className="px-3 py-2 text-muted-foreground">
                                    {r.valid_from || "—"} → {r.valid_until || "∞"}
                                </td>
                                <td className="px-3 py-2 text-right">{r.used_count || 0} / {r.max_uses || "∞"}</td>
                                <td className="px-3 py-2 text-center">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                        {r.active ? "Aktiv" : "Joaktiv"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="7" className="py-8 text-center text-muted-foreground">Ende s&apos;ka kode promo</td></tr>}
                    </tbody>
                </table>
            </div>
            {open && <PromoModal onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}

function PromoModal({ onClose, onSaved }) {
    const [f, setF] = useState({ code: "", description: "", discount_pct: 10, discount_amount: 0, max_uses: 0, valid_from: "", valid_until: "", min_stay: 0 });
    const submit = async () => {
        try {
            await api.post("/revenue/promo-codes", f);
            toast.success("Kodi promo u krijua");
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <Modal title="Kod i ri promo" onClose={onClose}>
            <div className="space-y-3">
                <Input label="Kodi (me shkronja të mëdha)" value={f.code} onChange={(v) => setF({ ...f, code: v.toUpperCase() })} testid="promo-code" />
                <Input label="Përshkrimi" value={f.description} onChange={(v) => setF({ ...f, description: v })} testid="promo-desc" />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Zbritja %" type="number" value={f.discount_pct} onChange={(v) => setF({ ...f, discount_pct: parseFloat(v) || 0 })} testid="promo-pct" />
                    <Input label="OSE shumë fikse" type="number" value={f.discount_amount} onChange={(v) => setF({ ...f, discount_amount: parseFloat(v) || 0 })} testid="promo-amt" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Vlefshëm nga" type="date" value={f.valid_from} onChange={(v) => setF({ ...f, valid_from: v })} />
                    <Input label="Vlefshëm deri" type="date" value={f.valid_until} onChange={(v) => setF({ ...f, valid_until: v })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Përdorime maks (0 = ∞)" type="number" value={f.max_uses} onChange={(v) => setF({ ...f, max_uses: parseInt(v, 10) || 0 })} />
                    <Input label="Qëndrimi min (netë)" type="number" value={f.min_stay} onChange={(v) => setF({ ...f, min_stay: parseInt(v, 10) || 0 })} />
                </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost text-[12px]">Anulo</button>
                <button onClick={submit} className="akm-btn-primary text-[12px]" data-testid="promo-submit">Krijo</button>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Suggestions
// ─────────────────────────────────────────────────────────────
function SuggestionsTab() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [selected, setSelected] = useState(new Set());
    const [generating, setGenerating] = useState(false);
    const [mode, setMode] = useState("balanced");
    const [days, setDays] = useState(30);

    const load = async () => {
        const { data } = await api.get(`/revenue/suggestions?status=${filter}`);
        setRows(data);
        setSelected(new Set());
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/revenue/suggestions/generate", { days, mode });
            toast.success(`${data.count} sugjerime u gjeneruan (${data.mode})`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setGenerating(false); }
    };

    const accept = async (id) => {
        await api.post(`/revenue/suggestions/${id}/accept`);
        toast.success("U aplikua");
        await load();
    };
    const reject = async (id) => {
        await api.post(`/revenue/suggestions/${id}/reject`, {});
        await load();
    };
    const acceptBulk = async () => {
        if (selected.size === 0) return toast.error("Asgjë e zgjedhur");
        const { data } = await api.post("/revenue/suggestions/accept-bulk", { ids: [...selected] });
        toast.success(`${data.accepted} u aplikuan`);
        await load();
    };

    const toggleAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map((r) => r.id)));
    };
    const toggle = (id) => {
        const ns = new Set(selected);
        if (ns.has(id)) ns.delete(id);
        else ns.add(id);
        setSelected(ns);
    };

    return (
        <div className="space-y-4" data-testid="revenue-suggestions">
            <div className="akm-card flex flex-wrap items-end gap-3 p-3">
                <Field label="Modaliteti">
                    <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="sug-mode"
                            className="akm-input text-[13px] capitalize">
                        {MODES.filter((m) => m.key !== "manual").map((m) => (
                            <option key={m.key} value={m.key}>{m.label}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Horizonti (ditë)">
                    <input type="number" min="1" max="90" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}
                           data-testid="sug-days" className="akm-input text-[13px]" />
                </Field>
                <button onClick={generate} disabled={generating} className="akm-btn-primary text-[12px]" data-testid="sug-generate">
                    <Wand2 size={12} /> {generating ? "Duke gjeneruar…" : "Gjenero sugjerime"}
                </button>
                <div className="ml-auto flex items-center gap-1">
                    {[
                        { k: "pending", l: "Në pritje" },
                        { k: "accepted", l: "Të pranuara" },
                        { k: "rejected", l: "Të refuzuara" },
                        { k: "all", l: "Të gjitha" },
                    ].map(({ k, l }) => (
                        <button key={k} onClick={() => setFilter(k)} data-testid={`sug-filter-${k}`}
                                className={`rounded-md border px-2.5 py-1 text-[11px] font-medium ${
                                    filter === k ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" : "border-border text-muted-foreground hover:bg-muted/40"
                                }`}>
                            {l}
                        </button>
                    ))}
                </div>
            </div>

            {filter === "pending" && rows.length > 0 && (
                <div className="flex items-center gap-2">
                    <button onClick={toggleAll} className="akm-btn-ghost text-[11px]">
                        {selected.size === rows.length ? "Çzgjidh të gjitha" : "Zgjidh të gjitha"}
                    </button>
                    <button onClick={acceptBulk} className="akm-btn-primary text-[11px]" data-testid="sug-bulk-accept">
                        <Check size={12} /> Prano të zgjedhurat ({selected.size})
                    </button>
                </div>
            )}

            <div className="akm-card overflow-hidden">
                <div className="max-h-[640px] overflow-auto">
                    <table className="w-full text-[13px]">
                        <thead className="sticky top-0 bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                {filter === "pending" && <th className="px-3 py-2 w-8" />}
                                <th className="px-3 py-2 text-left">Data</th>
                                <th className="px-3 py-2 text-left">Dhoma</th>
                                <th className="px-3 py-2 text-right">Aktual</th>
                                <th className="px-3 py-2 text-right">Sugjeruar</th>
                                <th className="px-3 py-2 text-right">Δ</th>
                                <th className="px-3 py-2 text-right">Mbushja</th>
                                <th className="px-3 py-2 text-left">Arsyeja</th>
                                <th className="px-3 py-2 text-left">Statusi</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody data-testid="sug-table">
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                                    {filter === "pending" && (
                                        <td className="px-3 py-2">
                                            <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                                        </td>
                                    )}
                                    <td className="px-3 py-2">{r.date}</td>
                                    <td className="px-3 py-2 text-muted-foreground">{r.room_number} · {r.room_type}</td>
                                    <td className="px-3 py-2 text-right">{formatCurrency(r.current_price)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{formatCurrency(r.suggested_price)}</td>
                                    <td className={`px-3 py-2 text-right font-semibold ${r.delta_pct > 0 ? "text-emerald-700" : "text-red-700"}`}>
                                        {r.delta_pct > 0 ? "+" : ""}{r.delta_pct}%
                                    </td>
                                    <td className="px-3 py-2 text-right text-muted-foreground">{r.occupancy_pct}%</td>
                                    <td className="px-3 py-2 text-[11px] text-muted-foreground">{r.reason}</td>
                                    <td className="px-3 py-2">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                            r.status === "pending" ? "bg-amber-50 text-amber-700" :
                                            r.status === "accepted" ? "bg-emerald-50 text-emerald-700" :
                                            "bg-slate-100 text-slate-500"
                                        }`}>{
                                            r.status === "pending" ? "në pritje" :
                                            r.status === "accepted" ? "e pranuar" :
                                            r.status === "rejected" ? "e refuzuar" : r.status
                                        }</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {r.status === "pending" && (
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => accept(r.id)} className="rounded-md bg-emerald-50 p-1 text-emerald-700 hover:bg-emerald-100" data-testid={`sug-accept-${r.id}`} title="Prano">
                                                    <Check size={13} />
                                                </button>
                                                <button onClick={() => reject(r.id)} className="rounded-md bg-red-50 p-1 text-red-700 hover:bg-red-100" data-testid={`sug-reject-${r.id}`} title="Refuzo">
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={filter === "pending" ? 10 : 9} className="py-10 text-center text-muted-foreground">
                                    S&apos;ka sugjerime {filter === "pending" ? "në pritje" : filter === "accepted" ? "të pranuara" : filter === "rejected" ? "të refuzuara" : ""}. Kliko &quot;Gjenero&quot; më lart për të nisur motorin.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Forecast
// ─────────────────────────────────────────────────────────────
function ForecastTab() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/revenue/forecast?days=${days}`).then((r) => setData(r.data));
    }, [days]);
    if (!data) return <div className="text-sm text-muted-foreground">Duke u ngarkuar…</div>;
    return (
        <div className="space-y-4" data-testid="revenue-forecast">
            <div className="flex items-center gap-2">
                {[30, 60, 90].map((d) => (
                    <button key={d} onClick={() => setDays(d)} data-testid={`forecast-${d}`}
                            className={`rounded-md border px-3 py-1.5 text-[12px] font-medium ${
                                days === d ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" : "border-border text-muted-foreground"
                            }`}>
                        {d} ditë
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="forecast-kpis">
                {Object.entries(data.summary).map(([k, v]) => {
                    const KPI_ALIAS = {
                        total_revenue: "Të ardhurat totale",
                        avg_occupancy_pct: "Mbushja mesatare",
                        avg_adr: "ADR mesatare",
                        avg_revpar: "RevPAR mesatare",
                        peak_revenue: "Të ardhura kulmore",
                        low_revenue: "Të ardhura minimale",
                        total_bookings: "Rezervime totale",
                        forecast_days: "Ditë të parashikuara",
                    };
                    const label = KPI_ALIAS[k] || k.replace(/_/g, " ");
                    return (
                        <div key={k} className="akm-card p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                            <div className="mt-0.5 text-lg font-semibold tracking-tight">
                                {typeof v === "number" ? (k.includes("revenue") || k.includes("adr") || k.includes("revpar") ? formatCurrency(v) : k.includes("pct") ? `${v}%` : v) : v}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ChartCard title="Të ardhurat e parashikuara (ditore)">
                    <AreaChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <defs><linearGradient id="fre" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ff7b02" stopOpacity={0.25} />
                            <stop offset="100%" stopColor="#ff7b02" stopOpacity={0} />
                        </linearGradient></defs>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} width={50} />
                        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} labelFormatter={formatDate} />
                        <Area type="monotone" dataKey="forecast_revenue" stroke="#ff7b02" strokeWidth={2} fill="url(#fre)" />
                    </AreaChart>
                </ChartCard>
                <ChartCard title="Mbushja e parashikuar %">
                    <BarChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} width={36} domain={[0, 100]} />
                        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => `${v}%`} labelFormatter={formatDate} />
                        <Bar dataKey="occupancy_pct" fill="#ff7b02" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ChartCard>
                <ChartCard title="ADR & RevPAR">
                    <LineChart data={data.rows} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} width={48} />
                        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatCurrency(v)} labelFormatter={formatDate} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="adr" stroke="#ff7b02" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="revpar" stroke="#2563EB" strokeWidth={2} dot={false} />
                    </LineChart>
                </ChartCard>
                <div className="akm-card overflow-hidden">
                    <div className="border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Ndarja ditore</div>
                    <div className="max-h-[260px] overflow-auto">
                        <table className="w-full text-[12px]">
                            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-2 py-1.5 text-left">Data</th>
                                    <th className="px-2 py-1.5 text-right">Mbushja</th>
                                    <th className="px-2 py-1.5 text-right">Të ardh.</th>
                                    <th className="px-2 py-1.5 text-right">ADR</th>
                                    <th className="px-2 py-1.5 text-right">RevPAR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((r) => (
                                    <tr key={r.date} className="border-t border-border">
                                        <td className="px-2 py-1">{r.date}</td>
                                        <td className="px-2 py-1 text-right">{r.occupancy_pct}%</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(r.forecast_revenue)}</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(r.adr)}</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(r.revpar)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children, height = 240 }) {
    return (
        <div className="akm-card p-4">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{title}</div>
            <div style={{ height }}>
                <ResponsiveContainer>{children}</ResponsiveContainer>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Shared form primitives
// ─────────────────────────────────────────────────────────────
function Input({ label, value, onChange, type = "text", step, testid }) {
    return (
        <label className="block">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid}
                   className="akm-input w-full text-[13px]" />
        </label>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="revenue-modal">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-xl border border-border bg-background p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-tight">{title}</div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X size={14} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}
